import { notification } from 'antd';
import { getSelectedProperty } from 'entities/property/property.selectors';
import { CreateStationSuccess } from 'entities/stations/stations.actions';
import StaticPointsMapper from 'pages/static-points/map/static-points-mapper';
import type { ActionsObservable, StateObservable } from 'redux-observable';
import { ofType } from 'redux-observable';
import type { AppState } from 'redux/app-state';
import { EMPTY, of } from 'rxjs';
import { catchError, concatMap, exhaustMap, map, switchMap, takeUntil, withLatestFrom } from 'rxjs/operators';
import type { CreateStaticPoint, LoadStaticPoints, LoadStaticPointsTemplates } from './static-points.actions';
import {
  LoadStaticPointsFailure,
  LoadStaticPointsSuccess,
  LoadStaticPointsTemplatesFailure,
  LoadStaticPointsTemplatesSuccess,
  StaticPointsActionsTypes
} from './static-points.actions';
import { StaticPointFieldEnum, StaticPointType } from '@protector/entities/static-point/constants';
import type { StaticPointCreation } from '@protector/entities/static-point/models';
import { StaticPointService } from './static-points.service';

const { getStaticPoints, createStaticPoint } = StaticPointService;
const { mapStaticPointToStation } = StaticPointsMapper;

export const handleLoadStaticPoint = (action$: ActionsObservable<ReturnType<typeof LoadStaticPoints>>, state$: StateObservable<AppState>) =>
  action$.pipe(
    ofType(StaticPointsActionsTypes.LOAD_STATIC_POINTS),
    map(action => action.payload),
    withLatestFrom(state$.pipe(map(getSelectedProperty))),
    exhaustMap(([payload, selectedProperty]) => {
      const propertyId = payload.propertyId ?? selectedProperty?.id ?? '';
      return getStaticPoints(propertyId, payload.fields).pipe(
        takeUntil(action$.pipe(ofType(StaticPointsActionsTypes.LOAD_STATIC_POINTS))),
        map(staticPoints => LoadStaticPointsSuccess(staticPoints)),
        catchError((error: string) => of(LoadStaticPointsFailure(error)))
      );
    })
  );

export const handleLoadStaticPointTemplate = (
  action$: ActionsObservable<ReturnType<typeof LoadStaticPointsTemplates>>,
  state$: StateObservable<AppState>
) =>
  action$.pipe(
    ofType(StaticPointsActionsTypes.LOAD_STATIC_POINTS_TEMPLATES),
    map(action => action.payload),
    withLatestFrom(state$.pipe(map(getSelectedProperty))),
    switchMap(([payload, selectedProperty]) => {
      const propertyId = payload.propertyId ?? selectedProperty?.id ?? '';
      return getStaticPoints(propertyId, [StaticPointFieldEnum.ALL, StaticPointFieldEnum.CATALOGUE], payload.dateRange).pipe(
        map(staticPoints => {
          const staticPointsTemplate = staticPoints.map(staticPoint => staticPoint);
          return LoadStaticPointsTemplatesSuccess(staticPointsTemplate);
        }),
        catchError((error: string) => of(LoadStaticPointsTemplatesFailure(error)))
      );
    })
  );

export const handleCreateStaticPoint = (
  action$: ActionsObservable<ReturnType<typeof CreateStaticPoint>>,
  state$: StateObservable<AppState>
) =>
  action$.pipe(
    ofType(StaticPointsActionsTypes.CREATE_STATIC_POINT),
    map(action => action.payload),
    withLatestFrom(state$.pipe(map(state => state.uiState.auth.user))),
    concatMap(([payload, user]) => {
      if (!user || payload.type !== StaticPointType.TRAP) return EMPTY;

      return createStaticPoint({ id: user.id, name: user.name }, payload.staticPoint).pipe(
        map(response => response.data),
        map((staticPoint: StaticPointCreation) => {
          const station = mapStaticPointToStation(payload.type, staticPoint);
          return CreateStationSuccess(station, payload.callback);
        }),
        catchError(() => {
          notification.error(payload.callback.notification.error);
          return EMPTY;
        })
      );
    })
  );
